import "@/sentry.client.config";
import { useRef, useEffect, useMemo, type ReactNode } from "react";
import { I18nextProvider } from "react-i18next";
import * as Sentry from "@sentry/react";
import { StatsigProvider } from "@statsig/react-bindings";
import {
  ChatMode,
  initPageStore,
  initUserStore,
  initConfigStore,
  PageCtx,
  AgentConfigCtx,
  UserCtx
} from "@/stores/page";
import type { Agent, BrandKit, User, VoiceAgent } from "@/lib/api/schema";
import { PROD_HOSTS, STATSIG_CLIENT_KEY } from "@/constants";
import AgentChat from "@/components/AgentChat";
import { type ApiBuildAgentConfig } from "@/types/buildAgent";
import i18n from "@/i18n";

// 获取要使用的 i18n 实例并设置服务端语言
const getI18nInstance = (initialLanguage?: string) => {
  if (initialLanguage && i18n.language !== initialLanguage) {
    i18n.changeLanguage(initialLanguage);
  }
  return i18n;
};

export const UserProvider = ({
  host,
  user,
  children,
  initialLanguage
}: {
  user: User | null;
  host: string;
  children: ReactNode;
  initialLanguage?: string;
}) => {
  const userStoreRef = useRef<ReturnType<typeof initUserStore>>(null);
  if (!userStoreRef.current) userStoreRef.current = initUserStore(user as User);

  const i18nInstance = useMemo(
    () => getI18nInstance(initialLanguage),
    [initialLanguage]
  );

  return (
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18nInstance} defaultNS={"translations"}>
        <UserCtx.Provider value={userStoreRef.current}>
          <StatsigProvider
            sdkKey={STATSIG_CLIENT_KEY}
            user={{
              userID: performance.now().toString()
            }}
            options={{
              environment: {
                tier: PROD_HOSTS.includes(host) ? "production" : "development"
              }
            }}
          >
            {children}
          </StatsigProvider>
        </UserCtx.Provider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  );
};

export const ConfigProvider = ({
  host,
  config,
  children,
  initialLanguage
}: {
  config: null | BrandKit;
  host: string;
  children: ReactNode;
  initialLanguage?: string;
}) => {
  const configStoreRef = useRef<ReturnType<typeof initConfigStore>>(null);
  if (!configStoreRef.current)
    configStoreRef.current = initConfigStore(null, config);

  const i18nInstance = useMemo(
    () => getI18nInstance(initialLanguage),
    [initialLanguage]
  );

  return (
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18nInstance} defaultNS={"translations"}>
        <AgentConfigCtx.Provider value={configStoreRef.current}>
          <StatsigProvider
            sdkKey={STATSIG_CLIENT_KEY}
            user={{
              userID: performance.now().toString()
            }}
            options={{
              environment: {
                tier: PROD_HOSTS.includes(host) ? "production" : "development"
              }
            }}
          >
            {children}
          </StatsigProvider>
        </AgentConfigCtx.Provider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  );
};

export const UserPageProvider = ({
  host,
  agent,
  user,
  children,
  initialLanguage
}: {
  host: string;
  agent: null | Agent;
  user: User | null;
  children: ReactNode;
  initialLanguage?: string;
}) => {
  const pageStoreRef = useRef<ReturnType<typeof initPageStore>>(null);
  if (!pageStoreRef.current) pageStoreRef.current = initPageStore(agent);

  const userStoreRef = useRef<ReturnType<typeof initUserStore>>(null);
  if (!userStoreRef.current) userStoreRef.current = initUserStore(user as User);

  const i18nInstance = useMemo(
    () => getI18nInstance(initialLanguage),
    [initialLanguage]
  );

  // 将日志移到 useEffect 中避免每次渲染都打印
  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log("UserPageProvider initialized:", { agent, user });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18nInstance} defaultNS={"translations"}>
        <UserCtx.Provider value={userStoreRef.current}>
          <PageCtx.Provider value={pageStoreRef.current}>
            <StatsigProvider
              sdkKey={STATSIG_CLIENT_KEY}
              user={{
                userID: user?.id || performance.now().toString(),
                email: user?.email
              }}
              options={{
                environment: {
                  tier: PROD_HOSTS.includes(host) ? "production" : "development"
                }
              }}
            >
              {children}
            </StatsigProvider>
          </PageCtx.Provider>
        </UserCtx.Provider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  );
};

export const AgentChatProvider = ({
  host,
  id,
  mode,
  preview,
  agent,
  voiceAgent,
  config,
  brandkit,
  from_link,
  initialLanguage,
  clientIp,
  country
}: {
  host: string;
  id: string;
  mode: ChatMode;
  preview: boolean;
  agent: null | Agent;
  voiceAgent: null | VoiceAgent;
  config: null | ApiBuildAgentConfig;
  brandkit: null | BrandKit;
  from_link: string;
  initialLanguage?: string;
  clientIp?: string | null;
  country?: string | null;
}) => {
  const pageStoreRef = useRef<ReturnType<typeof initPageStore>>(null);
  if (!pageStoreRef.current)
    pageStoreRef.current = initPageStore(agent, voiceAgent);

  const configStoreRef = useRef<ReturnType<typeof initConfigStore>>(null);
  if (!configStoreRef.current)
    configStoreRef.current = initConfigStore(config, brandkit);

  const i18nInstance = useMemo(
    () => getI18nInstance(initialLanguage),
    [initialLanguage]
  );

  // 将日志移到 useEffect 中避免每次渲染都打印
  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log("AgentChatProvider initialized:", {
        agent,
        voiceAgent,
        config,
        brandkit
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18nInstance} defaultNS={"translations"}>
        <PageCtx.Provider value={pageStoreRef.current}>
          <AgentConfigCtx.Provider value={configStoreRef.current}>
            <StatsigProvider
              sdkKey={STATSIG_CLIENT_KEY}
              user={{
                userID: performance.now().toString()
              }}
              options={{
                environment: {
                  tier: PROD_HOSTS.includes(host) ? "production" : "development"
                }
              }}
            >
              <AgentChat
                id={id}
                mode={mode as ChatMode}
                preview={preview}
                from_link={from_link}
                clientIp={clientIp}
                country={country}
              />
            </StatsigProvider>
          </AgentConfigCtx.Provider>
        </PageCtx.Provider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  );
};

export const AppProvider = ({
  host,
  agent,
  voiceAgent,
  user,
  config,
  brandkit,
  children,
  initialLanguage
}: {
  host: string;
  agent: null | Agent;
  voiceAgent: null | VoiceAgent;
  user: User | null;
  config: null | ApiBuildAgentConfig;
  brandkit: null | BrandKit;
  children: ReactNode;
  initialLanguage?: string;
}) => {
  const pageStoreRef = useRef<ReturnType<typeof initPageStore>>(null);
  if (!pageStoreRef.current)
    pageStoreRef.current = initPageStore(agent, voiceAgent);

  const userStoreRef = useRef<ReturnType<typeof initUserStore>>(null);
  if (!userStoreRef.current) userStoreRef.current = initUserStore(user as User);

  const configStoreRef = useRef<ReturnType<typeof initConfigStore>>(null);
  if (!configStoreRef.current)
    configStoreRef.current = initConfigStore(config, brandkit);

  const i18nInstance = useMemo(
    () => getI18nInstance(initialLanguage),
    [initialLanguage]
  );

  // 将日志移到 useEffect 中避免每次渲染都打印
  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log("AppProvider initialized:", {
        agent,
        voiceAgent,
        user,
        config,
        brandkit
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18nInstance} defaultNS={"translations"}>
        <UserCtx.Provider value={userStoreRef.current}>
          <PageCtx.Provider value={pageStoreRef.current}>
            <AgentConfigCtx.Provider value={configStoreRef.current}>
              <StatsigProvider
                sdkKey={STATSIG_CLIENT_KEY}
                user={{
                  userID: user?.id || performance.now().toString(),
                  email: user?.email
                }}
                options={{
                  environment: {
                    tier: PROD_HOSTS.includes(host)
                      ? "production"
                      : "development"
                  }
                }}
              >
                {children}
              </StatsigProvider>
            </AgentConfigCtx.Provider>
          </PageCtx.Provider>
        </UserCtx.Provider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  );
};
